import { Box } from "../../primitives/box";
import { Text } from "../../primitives/text";
import { Icon } from "../icon";
import type { PillsProps } from "./types";
import { usePillsStyles } from "./styles";

export const Pills = ({
  variant = "solid",
  size = "sm",
  color = "neutral",
  label,
  children,
  icon,
  iconName,
  iconPosition = "left",
  iconSize,
  iconColor,
  pressable = false,
  disabled = false,
  selected = false,
  onPress,
  dismissible = false,
  onDismiss,
  dismissIcon = "visibilityOff",
  dismissIconSize,
  dismissIconColor,
  px,
  py,
  gap,
  margin,
  alignItems = "center",
  justifyContent = "center",
  borderRadius,
  borderWidth,
  borderColor,
  shape = "pill",
  id,
  testID,
  accessibilityLabel,
  "aria-label": ariaLabel,
}: PillsProps) => {
  const isInteractive = pressable || !!onPress || (dismissible && !!onDismiss);
  const isDisabled = disabled;

  const styles = usePillsStyles({
    variant,
    size,
    color: color as string,
    isInteractive,
    isDisabled,
    selected,
    px,
    py,
    gap,
    margin,
    alignItems,
    justifyContent,
    borderRadius,
    borderWidth,
    borderColor,
    shape,
  });

  const content =
    label && children ? (
      <Box flexDirection="column" gap={2}>
        <Text
          variant="label"
          size={styles.textSize}
          color={styles.labelColor}
          ellipsis
        >
          {label}
        </Text>
        {children}
      </Box>
    ) : label ? (
      <Text
        variant="label"
        size={styles.textSize}
        color={styles.labelColor}
        ellipsis
      >
        {label}
      </Text>
    ) : (
      (children ?? null)
    );

  const computedIconSize = iconSize ?? styles.iconSize;
  const computedIconColor = iconColor ?? styles.iconColor;

  const iconElement =
    icon ??
    (iconName ? (
      <Icon name={iconName} size={computedIconSize} color={computedIconColor} />
    ) : null);

  const showDismiss = dismissible && !!onDismiss;
  const computedDismissIconSize = dismissIconSize ?? styles.dismissIconSize;
  const computedDismissIconColor = dismissIconColor ?? styles.dismissIconColor;

  const dismissButton = showDismiss ? (
    <Box
      as="button"
      // BoxProps doesn't currently include the native button `type` attribute.
      // Keep it as a non-submitting button when rendered inside forms.
      {...({ type: "button" } as unknown as Record<string, unknown>)}
      aria-label="Dismiss"
      data-testid={testID ? `${testID}-dismiss` : undefined}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!isDisabled) onDismiss?.();
      }}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 0,
        border: "none",
        background: "transparent",
        cursor: isDisabled ? "default" : "pointer",
      }}
      disabled={isDisabled}
    >
      <Icon
        name={dismissIcon}
        size={computedDismissIconSize}
        color={computedDismissIconColor}
      />
    </Box>
  ) : null;

  const orderedChildren =
    iconPosition === "right" ? (
      <>
        {content}
        {iconElement}
        {dismissButton}
      </>
    ) : (
      <>
        {iconElement}
        {content}
        {dismissButton}
      </>
    );

  const commonProps = {
    id,
    "data-testid": testID,
    "aria-label": ariaLabel ?? accessibilityLabel,
    style: styles.container,
  } as const;

  return isInteractive ? (
    <Box
      as="button"
      {...({ type: "button" } as unknown as Record<string, unknown>)}
      disabled={isDisabled}
      onClick={(e) => {
        if (isDisabled) {
          e.preventDefault();
          return;
        }
        onPress?.();
      }}
      {...commonProps}
    >
      {orderedChildren}
    </Box>
  ) : (
    <Box as="span" {...commonProps}>
      {orderedChildren}
    </Box>
  );
};

Pills.displayName = "Pills";
